import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { BrokerUserData } from '@partner/broker/domain/models/broker.model';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import { DashboardFilters, DashboardPeriod, DashboardScope } from '../../domain/model/dashboard-filters.model';
import {
  ExecutiveSnapshot,
  ExecutiveSnapshotDataVm,
  ExecutiveSnapshotVm,
  ExecutiveSnapshotVmState,
  REQUIRING_ACTION_STATUSES,
  RequiringActionRowVm,
  ViewAllQuery,
} from '../../domain/model/executive-snapshot.model';
import {
  buildInactivityPanelVm,
  currencyDigits,
  daysBetween,
  fundedTileLabel,
  paceLine,
  periodTitle,
  waitBadgeTier,
} from '../../shared/dashboard-request.helper';
import {
  clampToNow,
  DashboardSelection,
  errorVm,
  loadingVm,
  sameSelection,
  toSelection,
} from '../../shared/dashboard-vm.helper';

const LOADING_VM: ExecutiveSnapshotVm = loadingVm<ExecutiveSnapshotDataVm>();

function fullName(user: BrokerUserData | null | undefined): string | undefined {
  const name = [user?.firstName, user?.lastName].filter((part) => !!part).join(' ');
  return name || undefined;
}

function toRowVm(now: Date): (row: ExecutiveSnapshot['requiringAction'][number]) => RequiringActionRowVm {
  return (row) => {
    const waitDays = daysBetween(row.waitingSinceUtc, now);
    return { ...row, waitDays, badgeTier: waitBadgeTier(waitDays), amountDigits: currencyDigits(row.amount) };
  };
}

function toDataVm(
  snapshot: ExecutiveSnapshot,
  selection: DashboardSelection,
  period: DashboardPeriod,
  now: Date,
  firstAgent: string | undefined,
): ExecutiveSnapshotVm {
  const viewAllQuery: ViewAllQuery = { brokerStatus: [...REQUIRING_ACTION_STATUSES] };
  if (firstAgent) {
    viewAllQuery.firstAgent = firstAgent;
  }
  return {
    state: ExecutiveSnapshotVmState.DATA,
    asOfUtc: clampToNow(snapshot.asOfUtc, now),
    errorMessage: null,
    data: {
      periodTitle: periodTitle(period, selection.range),
      fundedTileLabel: fundedTileLabel(period),
      funded: snapshot.funded,
      paceLine: paceLine(snapshot.funded.amount, snapshot.funded.priorYearAmount, selection.range),
      tiles: snapshot.tiles,
      rows: snapshot.requiringAction.map(toRowVm(now)),
      showViewAll: snapshot.requiringActionTotal > 0,
      viewAllQuery,
      inactivityPanel: buildInactivityPanelVm(snapshot.inactivity.daysSinceLastSubmission),
    },
  };
}

/**
 * Screen facade of the executive snapshot: turns the shared filters into one request per distinct
 * `(from, to, scope)` and a precomputed view model. Component-provided; lives as long as the home screen.
 */
@Injectable()
export class ExecutiveSnapshotFacade {
  /** loading → data | error per request; re-derived every clock tick so wait days stay current. */
  readonly vm$: Observable<ExecutiveSnapshotVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    // `dateRange$` derives from `filters$` synchronously, so a period change emits twice; the wire tuple dedupes it.
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.dateRange$]).pipe(
      map(toSelection),
      filter((selection): selection is DashboardSelection => selection !== null),
      distinctUntilChanged(sameSelection),
      switchMap((selection: DashboardSelection) => this.load(selection)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next filter change fetches again. */
  private load(selection: DashboardSelection): Observable<ExecutiveSnapshotVm> {
    return combineLatest([
      this.dashboardFacade.getExecutiveSnapshot$(selection.range, selection.scope),
      this.firstAgent$(selection.scope),
      this.filtersFacade.now$,
      this.filtersFacade.filters$,
    ]).pipe(
      map(([snapshot, firstAgent, now, filters]: [ExecutiveSnapshot, string | undefined, Date, DashboardFilters]) =>
        toDataVm(snapshot, selection, filters.period, now, firstAgent),
      ),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<ExecutiveSnapshotDataVm>(error))),
    );
  }

  /**
   * "View all" on My deals narrows the list by the caller's name (the list's existing free-text filter).
   * The broker is normally cached by the shell resolver, but `BrokerFacade` re-fetches it whenever that cache
   * has been cleared — so this can fail on its own. It only decorates a link: swallow the failure rather than
   * take a snapshot that succeeded to the error card.
   */
  private firstAgent$(scope: DashboardScope): Observable<string | undefined> {
    if (scope !== DashboardScope.MY_DEALS) {
      return of(undefined);
    }
    return this.dashboardFacade.getCurrentBrokerUser$().pipe(
      map(fullName),
      catchError(() => of(undefined)),
    );
  }
}
